// Variations.js - Category required while adding a variation (implant filter follows the category) + 10 per page pagination

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTags, FaSearch, FaPlus, FaTrash, FaEdit, FaCheckDouble, FaEllipsisV, FaStore, FaChevronRight } from 'react-icons/fa';
import { GiValley } from "react-icons/gi";
import { useParams, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import TablePagination from '../../components/TablePagination';
import './Variations.css';

// Har page par sirf itni rows
const PAGE_SIZE = 10;

// Last loaded variations of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const VARIATIONS_CACHE_PREFIX = 'variations_cache_';

const readVariationsCache = (id) => {
  if (!id || id === 'all') return null;
  try {
    const raw = localStorage.getItem(`${VARIATIONS_CACHE_PREFIX}${id}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeVariationsCache = (id, data) => {
  if (!id || id === 'all') return;
  try {
    localStorage.setItem(`${VARIATIONS_CACHE_PREFIX}${id}`, JSON.stringify(data));
  } catch (e) {}
};

const Variations = () => {
  const { shopId } = useParams();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVariation, setEditingVariation] = useState(null);
  const [selectedVariations, setSelectedVariations] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [variationName, setVariationName] = useState('');
  const [shopName, setShopName] = useState('');
  const [selectedShopId, setSelectedShopId] = useState('');
  const [variations, setVariations] = useState(() => readVariationsCache(shopId) || []);
  // True only while there is nothing to show yet (so "No variations found" never flashes)
  const [isLoading, setIsLoading] = useState(() => !readVariationsCache(shopId));
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [shops, setShops] = useState([]);
  const [ukShopId, setUkShopId] = useState(null);
  const [isSaving, setIsSaving] = useState(false); // Double-click rokne ke liye

  // Move To (copy to other shops)
  const [showMoveList, setShowMoveList] = useState(false);
  const [moveShopIds, setMoveShopIds] = useState([]);
  const [moveLoading, setMoveLoading] = useState(false);

  const hasLoadedShopsRef = useRef(false);
  const isMountedRef = useRef(true);
  const savingRef = useRef(false); // instant lock (state update turant nahi hota)
  const variationsRequestRef = useRef(0);

  const API_BASE_URL = 'https://super-backend.hira-tech.co.uk/api/product';

  const isAllShopsMode = shopId === 'all' || !shopId;

  const showSuccessAlert = (message) => {
    Swal.fire({ icon: 'success', title: 'Success!', text: message, timer: 2000, showConfirmButton: true, confirmButtonColor: '#3085d6' });
  };
  const showErrorAlert = (message) => {
    Swal.fire({ icon: 'error', title: 'Error!', text: message, confirmButtonColor: '#d33' });
  };
  const showWarningAlert = (message) => {
    Swal.fire({ icon: 'warning', title: 'Warning!', text: message, confirmButtonColor: '#f0ad4e' });
  };
  const showDeleteConfirmation = (variationName, isMultiple = false, count = 1) => {
    const title = isMultiple
      ? `Are you sure you want to delete ${count} variation(s)?`
      : `Are you sure you want to delete variation "${variationName}"?`;
    return Swal.fire({
      title: 'Are you sure?', text: title, icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6', confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
  };

  const findUkShop = (shopsList) => {
    const ukShop = shopsList.find(shop =>
      shop.shop_name?.toLowerCase().includes('uk') ||
      shop.shop_display?.toLowerCase().includes('uk')
    );
    return ukShop || shopsList[0];
  };

  const fetchShops = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/shops/`);
      if (response.ok) {
        const data = await response.json();
        const shopsArray = Array.isArray(data) ? data : data.results || [];
        if (isMountedRef.current) setShops(shopsArray);
        const ukShop = findUkShop(shopsArray);
        if (ukShop && isMountedRef.current) setUkShopId(ukShop.shop_id);
        return shopsArray;
      }
      return [];
    } catch (error) { return []; }
  }, []);

  // Categories of one shop (in Implant mode the global filter returns only implant categories)
  const fetchCategories = useCallback(async (targetShopId) => {
    if (!targetShopId) {
      if (isMountedRef.current) setCategories([]);
      return [];
    }
    try {
      const response = await fetch(`${API_BASE_URL}/categories/?shop_id=${targetShopId}`);
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : data.results || [];
        if (isMountedRef.current) setCategories(list);
        return list;
      }
    } catch (error) { /* ignore */ }
    if (isMountedRef.current) setCategories([]);
    return [];
  }, []);

  const fetchVariations = useCallback(async (targetShopId) => {
    // Only the latest request is allowed to update the screen
    const requestId = ++variationsRequestRef.current;

    try {
      let url = `${API_BASE_URL}/variations/`;
      let shopIdToUse = targetShopId;

      if (!shopIdToUse) {
        const match = location.pathname.match(/\/shop\/(\d+)/);
        const currentShopId = match ? match[1] : null;
        if (currentShopId && currentShopId !== 'all') shopIdToUse = currentShopId;
        else if (ukShopId) shopIdToUse = ukShopId;
      }

      const queryParams = new URLSearchParams();
      if (shopIdToUse) queryParams.append('shop_id', shopIdToUse);

      const queryString = queryParams.toString();
      if (queryString) url += `?${queryString}`;

      const response = await fetch(url);
      if (requestId !== variationsRequestRef.current) return;

      if (response.ok) {
        const data = await response.json();
        if (requestId !== variationsRequestRef.current) return;

        const variationsData = Array.isArray(data) ? data : data.results || [];
        if (isMountedRef.current) setVariations(variationsData);
        writeVariationsCache(shopIdToUse, variationsData);
      } else {
        if (isMountedRef.current) setVariations([]);
      }
    } catch (error) {
      if (requestId === variationsRequestRef.current && isMountedRef.current) setVariations([]);
    } finally {
      if (requestId === variationsRequestRef.current && isMountedRef.current) setIsLoading(false);
    }
  }, [location.pathname, ukShopId]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      // Shop is already known from the URL: show the saved list right away
      // and load the shops and the variations together.
      if (shopId && shopId !== 'all') {
        const cached = readVariationsCache(shopId);
        if (cached) {
          setVariations(cached);
          setIsLoading(false);
        } else {
          setVariations([]);
          setIsLoading(true);
        }
        setSelectedShopId(shopId);

        const shopsPromise = hasLoadedShopsRef.current ? Promise.resolve(shops) : fetchShops();
        const [shopsResult] = await Promise.all([shopsPromise, fetchVariations(shopId)]);
        hasLoadedShopsRef.current = true;
        if (cancelled) return;

        const foundKnownShop = (shopsResult || []).find(s => s.shop_id === parseInt(shopId));
        if (foundKnownShop) setShopName(foundKnownShop.shop_name);
        return;
      }

      // No shop in the URL: the shops list is needed to pick the default shop
      let shopsData = shops;
      if (!hasLoadedShopsRef.current) {
        shopsData = await fetchShops();
        hasLoadedShopsRef.current = true;
      }
      if (cancelled) return;

      let currentShopId = null;
      const ukShop = findUkShop(shopsData);
      if (ukShop) currentShopId = ukShop.shop_id.toString();

      if (!currentShopId || currentShopId === 'all') {
        if (!cancelled) {
          setVariations([]);
          setIsLoading(false);
        }
        return;
      }

      const cachedDefault = readVariationsCache(currentShopId);
      if (cachedDefault && !cancelled) {
        setVariations(cachedDefault);
        setIsLoading(false);
      }

      const foundShop = shopsData.find(s => s.shop_id === parseInt(currentShopId));
      if (foundShop && !cancelled) setShopName(foundShop.shop_name);
      if (!cancelled) setSelectedShopId(currentShopId);

      await fetchVariations(currentShopId);
      if (cancelled) return;
    };

    loadData();
    return () => { cancelled = true; };
  }, [shopId]);

  useEffect(() => {
    const handleShopChangedEvent = async (event) => {
      if (event.detail && event.detail.shopId) {
        const newShopId = event.detail.shopId.toString();
        setSelectedShopId(newShopId);
        setPage(1);

        const cached = readVariationsCache(newShopId);
        if (cached) {
          setVariations(cached);
          setIsLoading(false);
        } else {
          setVariations([]);
          setIsLoading(true);
        }

        await fetchVariations(newShopId);

        const foundShop = shops.find(s => s.shop_id === parseInt(newShopId));
        if (foundShop && isMountedRef.current) setShopName(foundShop.shop_name);
      }
    };

    window.addEventListener('shopChanged', handleShopChangedEvent);
    return () => { window.removeEventListener('shopChanged', handleShopChangedEvent); };
  }, [shops]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const filteredVariations = variations.filter(variation =>
    variation.variation_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    variation.category_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ✅ 10 per page
  const totalPages = Math.max(1, Math.ceil(filteredVariations.length / PAGE_SIZE));
  const paginatedVariations = filteredVariations.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Shop badalne par pehle page par
  useEffect(() => {
    setPage(1);
  }, [shopId]);

  // Delete / search se pages kam ho jayein to page number bahar na reh jaye
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Shop currently being viewed
  const viewedShopId = shopId && shopId !== 'all' ? shopId : ukShopId;

  // All shops except the current one
  const otherShops = shops.filter(s => String(s.shop_id) !== String(viewedShopId));

  const resetMoveState = () => {
    setShowMoveList(false);
    setMoveShopIds([]);
  };

  const handleSelectAll = () => {
    if (selectedVariations.length === filteredVariations.length) setSelectedVariations([]);
    else setSelectedVariations(filteredVariations.map(v => v.variation_id));
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleSelectVariation = (variationId) => {
    if (selectedVariations.includes(variationId)) {
      setSelectedVariations(selectedVariations.filter(id => id !== variationId));
    } else {
      setSelectedVariations([...selectedVariations, variationId]);
    }
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleToggleMoveShop = (id) => {
    const key = String(id);
    if (moveShopIds.includes(key)) {
      setMoveShopIds(moveShopIds.filter(x => x !== key));
    } else {
      setMoveShopIds([...moveShopIds, key]);
    }
  };

  const handleMoveSelected = async () => {
    if (selectedVariations.length === 0) {
      showWarningAlert('Please select variations first');
      return;
    }
    if (moveShopIds.length === 0) {
      showWarningAlert('Please select at least one shop');
      return;
    }

    setMoveLoading(true);

    const variationsToCopy = variations.filter(v => selectedVariations.includes(v.variation_id));
    let successCount = 0;
    let failedCount = 0;

    try {
      for (const targetId of moveShopIds) {
        // Categories of the target shop (category ids are shop specific, so match by name)
        let targetCategories = [];
        try {
          const catRes = await fetch(`${API_BASE_URL}/categories/?shop_id=${targetId}`);
          if (catRes.ok) {
            const catData = await catRes.json();
            targetCategories = Array.isArray(catData) ? catData : catData.results || [];
          }
        } catch (err) {}

        for (const variation of variationsToCopy) {
          const match = targetCategories.find(
            c => c.category_Name?.toLowerCase() === (variation.category_name || '').toLowerCase()
          );

          if (!match) {
            failedCount++;
            continue;
          }

          try {
            const response = await fetch(`${API_BASE_URL}/variations/create/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                variation_name: variation.variation_name,
                shop_id: parseInt(targetId),
                category_id: match.catrgort_id
              }),
            });
            if (response.ok) successCount++;
            else failedCount++;
          } catch (err) {
            failedCount++;
          }
        }
      }

      if (successCount > 0 && failedCount === 0) {
        showSuccessAlert(`${successCount} variation(s) copied successfully!`);
      } else if (successCount > 0) {
        showWarningAlert(`${successCount} copied, ${failedCount} failed (category not found or already exists).`);
      } else {
        showErrorAlert('Could not copy variations (category not found in those shops, or they already exist).');
      }

      setSelectedVariations([]);
      setShowActionDropdown(false);
      resetMoveState();
      if (viewedShopId) await fetchVariations(viewedShopId);
    } finally {
      setMoveLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedVariations.length === 0) {
      showWarningAlert('Please select variations to delete');
      return;
    }
    const result = await showDeleteConfirmation(null, true, selectedVariations.length);
    if (result.isConfirmed) {
      try {
        let deletedCount = 0;
        for (const variationId of selectedVariations) {
          const response = await fetch(`${API_BASE_URL}/variations/${variationId}/delete/`, { method: 'DELETE' });
          if (response.ok || response.status === 204) deletedCount++;
        }
        showSuccessAlert(`${deletedCount} variation(s) deleted successfully!`);
        setSelectedVariations([]);
        setShowActionDropdown(false);
        if (selectedShopId) await fetchVariations(selectedShopId);
        else if (ukShopId) await fetchVariations(ukShopId);
      } catch (error) {
        showErrorAlert('Failed to delete some variations. Please try again.');
      }
    }
  };

  const handleClearAll = () => {
    setSelectedVariations([]);
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleOpenModalForNew = async () => {
    setEditingVariation(null);
    setVariationName('');
    setSelectedCategoryId('');
    const modalShopId = isAllShopsMode ? '' : shopId;
    setSelectedShopId(modalShopId || '');
    setCategories([]);
    setIsModalOpen(true);
    if (modalShopId) await fetchCategories(modalShopId);
  };

  const handleOpenModalForEdit = async (variation) => {
    setEditingVariation(variation);
    setVariationName(variation.variation_name);
    const shopIdVal = variation.shop?.shop_id?.toString() || '';
    setSelectedShopId(shopIdVal);
    setSelectedCategoryId(variation.category ? String(variation.category) : '');
    setIsModalOpen(true);
    await fetchCategories(shopIdVal);
  };

  const handleVariationNameChange = (e) => setVariationName(e.target.value);

  const handleShopChangeInModal = async (e) => {
    const newShopId = e.target.value;
    setSelectedShopId(newShopId);
    setSelectedCategoryId('');
    await fetchCategories(newShopId);
  };

  const handleSave = async () => {
    // Instant lock: a second click is blocked immediately (no duplicate request)
    if (savingRef.current || isSaving) return;

    if (!variationName.trim()) {
      showWarningAlert('Please enter Variation Name');
      return;
    }

    if (!selectedCategoryId) {
      showWarningAlert('Please select a category for this variation');
      return;
    }

    let targetShopId;
    if (editingVariation) targetShopId = editingVariation.shop?.shop_id;
    else if (isAllShopsMode) targetShopId = parseInt(selectedShopId);
    else targetShopId = parseInt(shopId);

    if (!targetShopId && isAllShopsMode) targetShopId = ukShopId;
    if (!targetShopId) {
      showWarningAlert('Please select a shop');
      return;
    }

    savingRef.current = true;
    setIsSaving(true); // Save process shuru

    try {
      const postData = {
        variation_name: variationName.trim(),
        shop_id: targetShopId,
        category_id: parseInt(selectedCategoryId)
      };

      if (editingVariation) {
        const response = await fetch(`${API_BASE_URL}/variations/${editingVariation.variation_id}/update/`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(postData),
        });
        if (response.ok) {
          showSuccessAlert('Variation updated successfully!');
          setIsModalOpen(false);
          setEditingVariation(null);
          setVariationName('');
          setSelectedCategoryId('');
          await fetchVariations(targetShopId);
        } else {
          const errorData = await response.json().catch(() => ({}));
          showErrorAlert(errorData.error || 'Failed to update variation');
        }
      } else {
        const response = await fetch(`${API_BASE_URL}/variations/create/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(postData),
        });
        if (response.ok) {
          showSuccessAlert('Variation added successfully!');
          setIsModalOpen(false);
          setVariationName('');
          setSelectedCategoryId('');
          await fetchVariations(targetShopId);
        } else {
          const errorData = await response.json().catch(() => ({}));
          showErrorAlert(errorData.error || 'Failed to add variation');
        }
      }
    } catch (error) {
      showErrorAlert('An error occurred. Please try again.');
    } finally {
      savingRef.current = false;
      setIsSaving(false); // Save process khatam
    }
  };

  const handleDelete = async () => {
    if (editingVariation) {
      const result = await showDeleteConfirmation(editingVariation.variation_name);
      if (result.isConfirmed) {
        try {
          const response = await fetch(`${API_BASE_URL}/variations/${editingVariation.variation_id}/delete/`, { method: 'DELETE' });
          if (response.ok || response.status === 204) {
            showSuccessAlert('Variation deleted successfully!');
            setIsModalOpen(false);
            setEditingVariation(null);
            setSelectedVariations(selectedVariations.filter(id => id !== editingVariation.variation_id));
            setVariationName('');
            if (selectedShopId) await fetchVariations(selectedShopId);
            else if (ukShopId) await fetchVariations(ukShopId);
          } else {
            showErrorAlert('Failed to delete variation');
          }
        } catch (error) {
          showErrorAlert('An error occurred. Please try again.');
        }
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingVariation(null);
    setVariationName('');
    setSelectedCategoryId('');
  };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
      resetMoveState();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showActionDropdown]);

  const getShopDisplayName = (variation) => {
    if (variation.shop) return `${variation.shop.shop_name} (ID: ${variation.shop.shop_id})`;
    return 'No Shop';
  };

  return (
    <div className="variations-main-container">

      {/* Heading + Search + Buttons */}
      <div className="variations-action-section">
        <div className="variations-heading">
          <h2>
            <GiValley className="variations-heading-icon" />
            Variations Management
          </h2>
        </div>

        <div className="variations-action-right">
          <div className="variations-search-wrapper">
            <FaSearch className="variations-search-icon" />
            <input
              type="text"
              placeholder="Search by Variation / Category..."
              value={searchTerm}
              onChange={handleSearch}
              className="variations-search-input"
            />
          </div>

          <div className="variations-action-buttons">
            <button className="variations-select-all-btn" onClick={handleSelectAll} disabled={moveLoading}>
              <FaCheckDouble />
              <span>{selectedVariations.length === filteredVariations.length && filteredVariations.length > 0 ? 'Deselect All' : 'Select All'}</span>
            </button>
            <button className="variations-add-btn" onClick={handleOpenModalForNew} disabled={moveLoading}>
              <FaPlus />
              <span>ADD VARIATION</span>
            </button>
          </div>
        </div>
      </div>

      {selectedVariations.length > 0 && (
        <div className="selected-summary">
          <div className="summary-content">
            <span className="summary-icon">✓</span>
            <span className="summary-text">
              {selectedVariations.length} variation(s) selected
            </span>
            <div className="action-dropdown-container">
              <button
                className="action-dropdown-btn"
                onClick={() => {
                  setShowActionDropdown(!showActionDropdown);
                  resetMoveState();
                }}
              >
                <FaEllipsisV />
                <span>Actions</span>
              </button>
              {showActionDropdown && (
                <div className="action-dropdown-menu">
                  <button
                    className="dropdown-item"
                    onClick={() => setShowMoveList(!showMoveList)}
                  >
                    <FaChevronRight className={`move-arrow ${showMoveList ? 'move-arrow-open' : ''}`} />
                    <span>Move To</span>
                  </button>

                  {showMoveList && (
                    <div className="move-shops-panel">
                      <div className="move-shops-list">
                        {otherShops.length > 0 ? (
                          otherShops.map(shop => (
                            <label key={shop.shop_id} className="move-shop-item">
                              <input
                                type="checkbox"
                                checked={moveShopIds.includes(String(shop.shop_id))}
                                onChange={() => handleToggleMoveShop(shop.shop_id)}
                                disabled={moveLoading}
                              />
                              <span>{shop.shop_name}</span>
                            </label>
                          ))
                        ) : (
                          <div className="move-no-shops">No other shops</div>
                        )}
                      </div>
                      {otherShops.length > 0 && (
                        <button
                          className="move-apply-btn"
                          onClick={handleMoveSelected}
                          disabled={moveLoading || moveShopIds.length === 0}
                        >
                          {moveLoading ? 'Copying...' : `Copy to ${moveShopIds.length} shop(s)`}
                        </button>
                      )}
                    </div>
                  )}

                  <button
                    className="dropdown-item delete-item"
                    onClick={handleDeleteSelected}
                  >
                    <FaTrash />
                    <span>Delete Selected ({selectedVariations.length})</span>
                  </button>
                </div>
              )}
            </div>
            <button className="clear-selection" onClick={handleClearAll}>
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* TABLE */}
      <div className="variations-table-container">
        <div className="variations-table-wrapper">
          <table className="variations-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={selectedVariations.length === filteredVariations.length && filteredVariations.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>VARIATION NAME</th>
                <th>CATEGORY</th>
                <th>SHOP</th>
                <th style={{ width: '100px' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {/* Nothing is shown until the first load is done, so "No variations found" never flashes */}
              {isLoading ? (
                <tr>
                  <td colSpan="5" style={{ height: '200px' }}></td>
                </tr>
              ) : paginatedVariations.length > 0 ? (
                paginatedVariations.map((variation) => (
                  <tr key={variation.variation_id} className={selectedVariations.includes(variation.variation_id) ? 'variations-selected-row' : ''}>
                    <td className="variations-checkbox-cell">
                      <input
                        type="checkbox"
                        checked={selectedVariations.includes(variation.variation_id)}
                        onChange={() => handleSelectVariation(variation.variation_id)}
                      />
                    </td>
                    <td className="variations-name-cell">
                      <span className="variations-name">{variation.variation_name}</span>
                    </td>
                    <td>{variation.category_name || '-'}</td>
                    <td>{getShopDisplayName(variation)}</td>
                    <td className="variations-actions-cell">
                      <button
                        className="variations-edit-btn"
                        onClick={() => handleOpenModalForEdit(variation)}
                        title="Edit Variation"
                      >
                        <FaEdit />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="variations-no-data-row">
                    <div className="variations-no-data">
                      <FaTags className="variations-no-data-icon" />
                      <p>No variations found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ✅ 10 per page:  1-10 of N   < Previous   Page 1 of N   Next > */}
      <TablePagination
        page={page}
        totalItems={filteredVariations.length}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      {/* MODAL - Class names are variations-* only */}
      {isModalOpen && (
        <div className="variations-modal-overlay" onClick={handleCloseModal}>
          <div className="variations-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="variations-modal-header">
              <h3>{editingVariation ? 'Edit Variation' : 'Add New Variation'}</h3>
              <button className="variations-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="variations-modal-body">
              <form className="variations-form" onSubmit={(e) => e.preventDefault()}>
                {isAllShopsMode && !editingVariation && (
                  <div className="variations-form-row">
                    <label className="variations-form-label"><FaStore className="variations-form-icon" />SELECT SHOP:</label>
                    <select className="variations-shop-select-dropdown" value={selectedShopId} onChange={handleShopChangeInModal} required>
                      <option value="">Select a shop</option>
                      {shops.map(shop => (
                        <option key={shop.shop_id} value={shop.shop_id}>
                          {shop.shop_display || `${shop.shop_name} (ID: ${shop.shop_id})`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(!isAllShopsMode || editingVariation) && (
                  <div className="variations-form-row">
                    <div className="variations-shop-info-display">
                      <span className="variations-shop-display-name">
                        {editingVariation && editingVariation.shop
                          ? `${editingVariation.shop.shop_name} (ID: ${editingVariation.shop.shop_id})`
                          : `${shopName} (ID: ${shopId || selectedShopId})`}
                      </span>
                    </div>
                  </div>
                )}

                <div className="variations-form-row">
                  <label className="variations-form-label">CATEGORY:</label>
                  <select
                    className="variations-shop-select-dropdown"
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    required
                  >
                    <option value="">
                      {(isAllShopsMode && !editingVariation && !selectedShopId)
                        ? 'Select a shop first'
                        : (categories.length ? 'Select a category' : 'No categories found')}
                    </option>
                    {categories.map(cat => (
                      <option key={cat.catrgort_id} value={cat.catrgort_id}>
                        {cat.category_Name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="variations-form-row">
                  <label className="variations-form-label">VARIATION NAME:</label>
                  <input
                    type="text"
                    name="variationName"
                    value={variationName}
                    onChange={handleVariationNameChange}
                    placeholder="Enter variation name (e.g., Tissue Level, Implant)"
                    autoFocus
                    className="variations-input"
                  />
                </div>
              </form>
            </div>
            <div className="variations-modal-footer">
              <button className="variations-btn-cancel" onClick={handleCloseModal}>Cancel</button>
              {editingVariation && (<button className="variations-btn-delete" onClick={handleDelete}>Delete</button>)}
              <button
                className="variations-btn-save"
                onClick={handleSave}
                disabled={isSaving} // Button disable ho jayega jab tak save ho raha ho
              >
                {isSaving ? 'Saving...' : (editingVariation ? 'Update Variation' : 'Save Variation')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Variations;